import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  providerWebhooksService,
  ProviderWebhooksRequestError,
} from '@/services/provider/provider-webhooks.service';
import type {
  ProviderWebhookDelivery,
  ProviderWebhookEndpoint,
  UpsertProviderWebhookInput,
} from '@/types/provider-webhooks';

export const providerWebhooksKeys = {
  all: ['provider', 'webhooks'] as const,
  list: () => [...providerWebhooksKeys.all, 'list'] as const,
  detail: (webhookId: string) => [...providerWebhooksKeys.all, 'detail', webhookId] as const,
  deliveries: (webhookId: string, limit: number) =>
    [...providerWebhooksKeys.all, 'deliveries', webhookId, limit] as const,
};

export function useProviderWebhooks(enabled = true) {
  return useQuery<ProviderWebhookEndpoint[], ProviderWebhooksRequestError>({
    queryKey: providerWebhooksKeys.list(),
    queryFn: () => providerWebhooksService.listWebhooks(),
    enabled,
  });
}

export function useCreateProviderWebhook() {
  const queryClient = useQueryClient();

  return useMutation<ProviderWebhookEndpoint, ProviderWebhooksRequestError, UpsertProviderWebhookInput>({
    mutationFn: (input) => providerWebhooksService.createWebhook(input),
    onSuccess: (webhook) => {
      queryClient.invalidateQueries({ queryKey: providerWebhooksKeys.list() });
      queryClient.setQueryData(providerWebhooksKeys.detail(webhook.id), webhook);
    },
  });
}

export function useUpdateProviderWebhook() {
  const queryClient = useQueryClient();

  return useMutation<
    ProviderWebhookEndpoint,
    ProviderWebhooksRequestError,
    { webhookId: string; input: UpsertProviderWebhookInput }
  >({
    mutationFn: ({ webhookId, input }) => providerWebhooksService.updateWebhook(webhookId, input),
    onSuccess: (webhook) => {
      queryClient.invalidateQueries({ queryKey: providerWebhooksKeys.list() });
      queryClient.setQueryData(providerWebhooksKeys.detail(webhook.id), webhook);
    },
  });
}

export function useDeleteProviderWebhook() {
  const queryClient = useQueryClient();

  return useMutation<void, ProviderWebhooksRequestError, string>({
    mutationFn: (webhookId) => providerWebhooksService.deleteWebhook(webhookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerWebhooksKeys.list() });
      queryClient.invalidateQueries({ queryKey: providerWebhooksKeys.all });
    },
  });
}

export function useProviderWebhookDeliveries(webhookId: string | null, limit = 25, enabled = true) {
  return useQuery<ProviderWebhookDelivery[], ProviderWebhooksRequestError>({
    queryKey: providerWebhooksKeys.deliveries(webhookId ?? 'none', limit),
    queryFn: () => providerWebhooksService.listDeliveries(webhookId ?? '', limit),
    enabled: Boolean(webhookId) && enabled,
  });
}
